import Component from '@glimmer/component';

import { action, get } from '@ember/object';
import { service } from '@ember/service';
import { cached } from '@glimmer/tracking';
import { decorateClass, isDecoratorArgs } from 'decorator-setup';

/**
 * LinkToAction input args
 * @typedef LinkToActionDecoratedComponentArgs
 * @property {{}} [args.action] LinkTo Action
 */
/**
 * LinkToAction properties
 * @typedef LinkToActionDecoratedComponentProperties
 * @property {string} mixClassPrefix Prefixes to add to mixed classes
 * @property {string} mixClassSuffix Suffixes to add to mixed classes
 * @property {Function} mixClassNames Mix given classes with attachments (not modifiers)
 * @property {Function} mixClassModifiers Combine given modifiers with mixed modifiers
 * @property {Function} mixClassModConfig Combine given classes and modifiers with mixed classes and modifiers as `class-mod` helper config
 * @property {Function} getParamsFromLinkToGenerator
 */
/**
 * @typedef LinkToActionDecoratedComponent
 * @type {LinkToActionDecoratedComponent & LinkToActionDecoratedComponentArgs & LinkToActionDecoratedComponentProperties}
 */

/**
 * Component class decorator to assist in calling component actions.
 * @decorator
 */
export function linkToAction() {
  function classDecorator(Target) {
    if (!Component.isPrototypeOf(Target)) {
      throw new Error(
        '@linkToAction decorator should be applied to an Ember Component derived class',
      );
    }
    return decorateClass(class LinkToActionDecoratedComponent extends Target {
      @service router;

      // Default link-to params for default properties
      @cached get paramsForLinkTo() {
        return this.getParamsForLinkTo(this.linkToParams);
      }
      get onLinkToAction() {
        return this.args.action;
      }

      @action callLinkToActionByName(name, ...args) {
        return this.callLinkToFn(get(this, name), ...args);
      }

      @action callLinkToAction(...args) {
        return this.callLinkToFn(this.onLinkToAction, ...args);
      }

      @action callLinkToFn(action, ...args) {
        if (typeof action === 'function') {
          return action(...args);
        }
      }

      // Get link-to params given params and additional params
      getParamsForLinkTo(params) {
        if (params && (!Array.isArray(params) || params.length)) {
          params = [].concat(params);
        } else {
          const route = this.router.currentRoute;
          params = [
            route.name,
            ...route.paramNames.map(paramName => route.params[paramName]),
          ];
        }
        return params;
      }

      // Get link-to params from given generator, if it is a function
      getParamsFromLinkToGenerator(generator, ...args) {
        let result = generator;
        if (typeof generator === 'function') {
          result = generator(...args);
        }
        return result;
      }
    });
  }
  return isDecoratorArgs(...arguments) ? classDecorator(...arguments) : classDecorator;
}
